import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { FireIcon } from '../components/UiIcons';

const TYPE_META = {
  total: {
    label: 'Total',
    short: 'Total streak',
    icon: '🔥',
    gradient: 'from-orange-500 via-red-500 to-rose-600',
    bar: 'bg-gradient-to-r from-orange-400 to-red-500',
    glow: 'shadow-orange-500/20',
    chip: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
    action: null,
    actionLabel: null,
  },
  login: {
    label: 'Login',
    short: 'Login streak',
    icon: '🔑',
    gradient: 'from-sky-500 to-blue-600',
    bar: 'bg-gradient-to-r from-sky-400 to-blue-500',
    glow: 'shadow-sky-500/20',
    chip: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    action: '/login',
    actionLabel: 'Log in today',
  },
  free: {
    label: 'Free',
    short: 'Free prediction',
    icon: '🎟️',
    gradient: 'from-emerald-500 to-teal-600',
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-500',
    glow: 'shadow-emerald-500/20',
    chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    action: '/',
    actionLabel: 'Pick a free match',
  },
  boost: {
    label: 'Boost',
    short: 'Boost streak',
    icon: '⚡',
    gradient: 'from-violet-500 to-purple-600',
    bar: 'bg-gradient-to-r from-violet-400 to-purple-500',
    glow: 'shadow-violet-500/20',
    chip: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    action: '/',
    actionLabel: 'Boost a match',
  },
};

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Play daily',
    text: 'Log in, place a free prediction, or boost a match — each counts as its own streak.',
  },
  {
    step: '2',
    title: 'Stack your total',
    text: 'Your total streak is Login + Free + Boost combined. Keep all three alive to climb faster.',
  },
  {
    step: '3',
    title: 'Unlock golden tickets',
    text: 'Hit milestone targets on your personal best. Rewards are one-time and land in your wallet automatically.',
  },
];

function streakRank(total) {
  if (total >= 100) return { name: 'Legend', emoji: '👑' };
  if (total >= 50) return { name: 'Champion', emoji: '🏆' };
  if (total >= 25) return { name: 'Veteran', emoji: '⭐' };
  if (total >= 10) return { name: 'Rising', emoji: '🚀' };
  if (total >= 1) return { name: 'Rookie', emoji: '🎯' };
  return { name: 'Newcomer', emoji: '✨' };
}

function StreakStatCard({ title, description, icon, current, best, meta, actionHref, actionLabel }) {
  const pct = best > 0 ? Math.min(100, Math.round((current / best) * 100)) : current > 0 ? 100 : 0;
  const atRisk = current === 0 && best > 0;

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-slate-900/90 shadow-lg ${meta.glow}`}
    >
      <div className={`absolute inset-0 opacity-[0.07] dark:opacity-20 bg-gradient-to-br ${meta.gradient}`} aria-hidden />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${meta.chip}`}
            >
              {meta.label}
            </span>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mt-2">{title}</h3>
            <p className="text-xs text-slate-500 dark:text-white/55 mt-1 leading-relaxed">{description}</p>
          </div>
          <span className="text-3xl shrink-0" aria-hidden>
            {icon}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl bg-slate-50 dark:bg-black/25 px-3 py-2.5 border border-slate-100 dark:border-white/5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/45">
              Current
            </p>
            <p className="text-3xl sm:text-4xl font-black tabular-nums text-slate-900 dark:text-white">{current}</p>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-black/25 px-3 py-2.5 border border-slate-100 dark:border-white/5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-white/45">
              Personal best
            </p>
            <p className="text-2xl sm:text-3xl font-bold tabular-nums text-slate-700 dark:text-white/90">{best}</p>
          </div>
        </div>

        <div className="mb-2 flex justify-between text-[10px] font-medium text-slate-400 dark:text-white/45">
          <span>Progress vs best</span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 dark:bg-black/30 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${meta.bar}`} style={{ width: `${pct}%` }} />
        </div>

        {atRisk && (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-300/90 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/40 rounded-lg px-2.5 py-2">
            Streak paused — complete today&apos;s action to start building again.
          </p>
        )}

        {actionHref && actionLabel && (
          <Link
            to={actionHref}
            className="mt-4 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-slate-700 to-slate-900 dark:from-white/10 dark:to-white/5 border border-slate-200 dark:border-white/10 hover:opacity-90 transition-opacity"
          >
            {actionLabel}
          </Link>
        )}
      </div>
    </article>
  );
}

function MilestoneCard({ milestone, isNext }) {
  const meta = TYPE_META[milestone.streakType] || TYPE_META.total;
  const pct = milestone.claimed ? 100 : Math.min(100, milestone.progressPct || 0);
  const remaining = Math.max(0, milestone.requiredStreak - (milestone.progressBest ?? 0));

  let statusLabel = 'In progress';
  let statusClass = 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/70';
  if (milestone.claimed) {
    statusLabel = 'Unlocked';
    statusClass = 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200';
  } else if (remaining === 0) {
    statusLabel = 'Ready';
    statusClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200';
  }

  return (
    <article
      className={`rounded-2xl border p-4 sm:p-5 transition-all ${
        milestone.claimed
          ? 'border-amber-300/60 dark:border-amber-500/40 bg-amber-50/80 dark:bg-amber-950/25'
          : isNext
            ? 'border-orange-400/50 dark:border-orange-500/40 bg-orange-50/50 dark:bg-orange-950/15 ring-2 ring-orange-400/20'
            : 'border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70'
      }`}
    >
      {isNext && !milestone.claimed && (
        <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600 dark:text-orange-400 mb-2">
          Next target
        </p>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xl" aria-hidden>
              {meta.icon}
            </span>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-white/60">
              {milestone.streakTypeLabel || meta.short}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusClass}`}>{statusLabel}</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-white/85">
            Reach <strong className="text-slate-900 dark:text-white">{milestone.requiredStreak}</strong> day
            {milestone.requiredStreak === 1 ? '' : 's'} on your best run
          </p>
        </div>
        <div className="text-right shrink-0 rounded-xl bg-amber-100/80 dark:bg-amber-500/10 px-3 py-2 border border-amber-200/60 dark:border-amber-500/20">
          <p className="text-2xl font-black text-amber-600 dark:text-amber-400 tabular-nums">
            +{milestone.goldenTickets}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700/70 dark:text-amber-300/70">
            Golden
          </p>
        </div>
      </div>

      <div className="h-2.5 rounded-full bg-slate-100 dark:bg-black/30 overflow-hidden mb-2">
        <div
          className={`h-full rounded-full transition-all duration-500 ${milestone.claimed ? 'bg-amber-400' : meta.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-slate-500 dark:text-white/50 tabular-nums">
        Best: {milestone.progressBest ?? 0} / {milestone.requiredStreak}
        {!milestone.claimed && remaining > 0 && (
          <span className="text-slate-400 dark:text-white/40"> · {remaining} to go</span>
        )}
      </p>
    </article>
  );
}

function ComboMeter({ login, free, boost, total }) {
  const sum = Math.max(1, login + free + boost);
  const segments = [
    { key: 'login', value: login, color: 'bg-sky-500', label: 'Login' },
    { key: 'free', value: free, color: 'bg-emerald-500', label: 'Free' },
    { key: 'boost', value: boost, color: 'bg-violet-500', label: 'Boost' },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-slate-900/60 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-bold text-slate-800 dark:text-white">How your total is built</p>
        <p className="text-lg font-black tabular-nums text-orange-600 dark:text-orange-400">{total}</p>
      </div>
      <div className="flex h-4 rounded-full overflow-hidden ring-1 ring-slate-200 dark:ring-white/10 mb-3">
        {segments.map((s) => (
          <div
            key={s.key}
            className={`${s.color} transition-all duration-500`}
            style={{ width: `${(s.value / sum) * 100}%`, minWidth: s.value > 0 ? '4px' : 0 }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-white/60">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${s.color}`} />
            {s.label} <strong className="text-slate-900 dark:text-white tabular-nums">{s.value}</strong>
          </span>
        ))}
        <span className="text-slate-400 dark:text-white/40">= total streak</span>
      </div>
    </div>
  );
}

const Streaks = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [publicMilestones, setPublicMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedAwardBanner, setDismissedAwardBanner] = useState(false);
  const fetchGenRef = useRef(0);

  const fetchMilestonesPublic = useCallback(async () => {
    try {
      const { data } = await api.get('/streaks/milestones');
      setPublicMilestones(Array.isArray(data?.milestones) ? data.milestones : []);
    } catch {
      setPublicMilestones([]);
    }
  }, []);

  const fetchUserStreak = useCallback(
    async ({ silent = false } = {}) => {
      if (!user?._id) {
        setStats(null);
        if (!silent) setLoading(false);
        return;
      }
      const gen = ++fetchGenRef.current;
      if (!silent) setLoading(true);
      try {
        const { data } = await api.get('/streaks/user');
        if (gen !== fetchGenRef.current) return;
        setStats(data);
      } catch (error) {
        if (gen !== fetchGenRef.current) return;
        console.error('Error fetching streaks:', error);
        setStats(null);
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [user?._id]
  );

  useEffect(() => {
    fetchMilestonesPublic();
  }, [fetchMilestonesPublic]);

  useEffect(() => {
    fetchUserStreak();
  }, [fetchUserStreak]);

  useEffect(() => {
    if (!user?._id) return undefined;
    const id = setInterval(() => fetchUserStreak({ silent: true }), 60000);
    return () => clearInterval(id);
  }, [user?._id, fetchUserStreak]);

  const milestones = useMemo(() => {
    if (stats?.milestones?.length) return stats.milestones;
    return publicMilestones.map((m) => ({
      ...m,
      claimed: false,
      reached: false,
      progressBest: 0,
      progressCurrent: 0,
      progressPct: 0,
    }));
  }, [stats?.milestones, publicMilestones]);

  const nextMilestoneId = useMemo(() => {
    const open = milestones.filter((m) => !m.claimed);
    if (!open.length) return null;
    open.sort((a, b) => (a.progressPct || 0) - (b.progressPct || 0) || a.requiredStreak - b.requiredStreak);
    const closest = open[open.length - 1];
    return closest?.id ?? null;
  }, [milestones]);

  const claimedCount = milestones.filter((m) => m.claimed).length;
  const newAwards = stats?.milestoneAwards || [];

  if (loading && user && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0e17]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <header className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Streak Arena</h1>
            <p className="text-sm text-slate-500 dark:text-white/50 mt-2">Loading your streak stats…</p>
          </header>
          <div className="py-12 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
          </div>
        </div>
      </div>
    );
  }

  const total = stats?.totalStreak ?? stats?.currentStreak ?? 0;
  const totalBest = stats?.bestStreak ?? stats?.longestStreak ?? 0;
  const login = stats?.login || { current: 0, best: 0 };
  const free = stats?.free || { current: 0, best: 0 };
  const boost = stats?.boost || { current: 0, best: 0 };
  const rank = streakRank(totalBest || total);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0e17] text-slate-900 dark:text-white">
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute -top-24 left-1/4 w-[28rem] h-[28rem] bg-orange-500/10 dark:bg-orange-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <header className="text-center mb-8 sm:mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-400 mb-2">
            Daily engagement
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">
            Streak{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600 dark:from-orange-400 dark:to-red-500">
              Arena
            </span>
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-white/60 max-w-2xl mx-auto leading-relaxed">
            Three daily habits. One combined score. Hit milestones to earn golden tickets for premium play.
          </p>
        </header>

        {/* How it works — always visible for clarity */}
        <section className="mb-8 sm:mb-10 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {HOW_IT_WORKS.map((item) => (
            <div
              key={item.step}
              className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 p-4 sm:p-5"
            >
              <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 text-sm font-black mb-3">
                {item.step}
              </span>
              <h2 className="font-bold text-slate-900 dark:text-white mb-1">{item.title}</h2>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-white/55 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </section>

        {!user ? (
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 p-8 sm:p-12 text-center max-w-lg mx-auto mb-10 shadow-lg">
            <FireIcon className="w-16 h-16 mx-auto text-orange-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Track your streaks</h2>
            <p className="text-slate-600 dark:text-white/60 mb-6 text-sm leading-relaxed">
              Sign in to see live streak counts, milestone progress, and golden ticket rewards.
            </p>
            <Link
              to="/login"
              className="inline-flex px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold hover:opacity-95 shadow-lg shadow-orange-500/25"
            >
              Log in to start
            </Link>
          </div>
        ) : (
          <>
            {newAwards.length > 0 && !dismissedAwardBanner && (
              <div className="mb-6 rounded-2xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-500/40 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-bold text-amber-900 dark:text-amber-200">Milestone unlocked!</p>
                  <p className="text-sm text-amber-800/80 dark:text-amber-200/70 mt-0.5">
                    You earned{' '}
                    {newAwards.reduce((s, a) => s + (a.goldenTickets || 0), 0)} golden ticket
                    {newAwards.reduce((s, a) => s + (a.goldenTickets || 0), 0) === 1 ? '' : 's'}.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDismissedAwardBanner(true)}
                  className="text-sm font-semibold text-amber-800 dark:text-amber-300 hover:underline shrink-0"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div className="mb-8 rounded-3xl border border-orange-200/80 dark:border-orange-500/30 bg-gradient-to-br from-orange-50 via-white to-red-50/50 dark:from-orange-950/30 dark:via-slate-900/80 dark:to-red-950/20 p-6 sm:p-8 lg:p-10 shadow-xl">
              <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/80 dark:bg-black/30 border border-orange-200 dark:border-orange-500/30 text-orange-800 dark:text-orange-200">
                      {rank.emoji} {rank.name}
                    </span>
                    {stats?.goldenTickets != null && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-500/30">
                        🎫 {stats.goldenTickets} golden
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-orange-600/90 dark:text-orange-300/90 mb-2">
                    Total streak today
                  </p>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <FireIcon className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 shrink-0" />
                    <p className="text-5xl sm:text-6xl lg:text-7xl font-black tabular-nums leading-none text-slate-900 dark:text-white">
                      {total}
                    </p>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-white/55 mt-3">
                    Combined personal best:{' '}
                    <strong className="text-slate-900 dark:text-white tabular-nums">{totalBest}</strong>
                  </p>
                </div>
                <div className="flex-1 w-full lg:max-w-md">
                  <ComboMeter
                    login={login.current}
                    free={free.current}
                    boost={boost.current}
                    total={total}
                  />
                </div>
              </div>
            </div>

            <section className="mb-10">
              <div className="flex items-end justify-between gap-4 mb-4 sm:mb-5">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Your streak lanes</h2>
                  <p className="text-sm text-slate-500 dark:text-white/50 mt-1">
                    Each lane resets if you miss a full UTC day without that activity.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                <StreakStatCard
                  title="Login streak"
                  description="Open the app and sign in once per UTC day."
                  icon="🔑"
                  current={login.current}
                  best={login.best}
                  meta={TYPE_META.login}
                  actionHref={null}
                  actionLabel={null}
                />
                <StreakStatCard
                  title="Free prediction streak"
                  description="Submit at least one free pick on any open match or poll."
                  icon="🎟️"
                  current={free.current}
                  best={free.best}
                  meta={TYPE_META.free}
                  actionHref="/"
                  actionLabel="Browse matches"
                />
                <StreakStatCard
                  title="Boost streak"
                  description="Place a boost stake on any open match or poll."
                  icon="⚡"
                  current={boost.current}
                  best={boost.best}
                  meta={TYPE_META.boost}
                  actionHref="/"
                  actionLabel="Find boost events"
                />
              </div>
            </section>
          </>
        )}

        {milestones.length > 0 && (
          <section className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  Golden ticket milestones
                </h2>
                <p className="text-sm text-slate-500 dark:text-white/50 mt-1 max-w-xl">
                  One-time rewards based on your <strong className="font-semibold">personal best</strong> streak
                  per category. Progress never drops when your current streak resets.
                </p>
              </div>
              {user && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-500/30">
                  <span className="text-2xl font-black text-amber-600 dark:text-amber-400 tabular-nums">
                    {claimedCount}/{milestones.length}
                  </span>
                  <span className="text-xs font-semibold text-amber-800/80 dark:text-amber-200/70 uppercase tracking-wide">
                    Unlocked
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {milestones.map((m) => (
                <MilestoneCard key={m.id} milestone={m} isNext={m.id === nextMilestoneId} />
              ))}
            </div>
          </section>
        )}

        <section className="mb-8 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/50 p-5 sm:p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-white/50 mb-3">
            Quick rules
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-white/60">
            <li className="flex gap-2">
              <span className="text-orange-500 shrink-0">•</span>
              Days are counted in UTC (resets at midnight UTC).
            </li>
            <li className="flex gap-2">
              <span className="text-orange-500 shrink-0">•</span>
              Skip one full day and that lane&apos;s current streak goes to 0.
            </li>
            <li className="flex gap-2">
              <span className="text-orange-500 shrink-0">•</span>
              Personal bests stay forever — milestones use your best run.
            </li>
            <li className="flex gap-2">
              <span className="text-orange-500 shrink-0">•</span>
              Total streak = login + free + boost (current counts added).
            </li>
          </ul>
        </section>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
          <Link
            to="/leaderboard"
            className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition-opacity w-full sm:w-auto"
          >
            View leaderboard
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold border border-slate-300 dark:border-white/20 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors w-full sm:w-auto"
          >
            Play today&apos;s matches
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Streaks;
